/**
 * Schedule-conflict indicator: alert dot with an opaque tooltip (trigger is the badge only).
 */

import * as React from 'react';
import { CalendarClock, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Props for {@link ConflictBadge}. */
interface ConflictBadgeProps {
  /** Detail message (e.g. already booked from 14:00 to 16:00). */
  hint: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

/** Badge showing a scheduling conflict hint. */
export const ConflictBadge: React.FC<ConflictBadgeProps> = ({ hint, side = 'top', className }) => {
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={hint}
            onPointerDown={(e) => e.stopPropagation()}
            className={cn(
              'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border cursor-help',
              'border-[rgb(var(--color-warning)_/_0.5)] bg-[rgb(var(--color-warning)_/_0.14)]',
              'text-[rgb(var(--color-warning))] transition-colors',
              'hover:bg-[rgb(var(--color-warning)_/_0.24)]',
              className,
            )}
          >
            <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={8}
          variant="light"
          className={cn(
            'z-[9999] max-w-[260px] rounded-xl border-2 px-3.5 py-3 shadow-xl',
            'border-[rgb(var(--color-warning)_/_0.35)]',
          )}
        >
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-warning)_/_0.16)]">
              <CalendarClock className="h-3.5 w-3.5 text-[rgb(var(--color-warning))]" />
            </span>
            <div className="space-y-0.5">
              <p className="text-xs font-bold text-[rgb(var(--color-warning))]">
                Conflit d&apos;horaire
              </p>
              <p className="text-xs leading-relaxed text-gray-600">{hint}</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
